import * as Sentry from "@sentry/react";
import axios from "axios";

/**
 * Sentry 設定集中在這裡。
 *
 * - Sentry org：nani-rd，project slug：questionbank-frontend
 * - DSN 由 .env-cmdrc 的 REACT_APP_SENTRY_DSN 提供；沒填就整組不啟用（本機開發預設不啟用）
 * - 依團隊 security policy，不把 PII（cookie、姓名、帳號密碼、API request/response body）送進 Sentry
 *
 * 注意：這個檔案在 import 時就會完成 init 與 axios interceptor 註冊，
 * 所以 index.js 必須把它放在第一個 import。
 */

const DSN = process.env.REACT_APP_SENTRY_DSN || "";
const ENVIRONMENT = process.env.REACT_APP_NODE_ENV || "local";
const RELEASE = process.env.REACT_APP_SENTRY_RELEASE || process.env.REACT_APP_VERSION || "";
const TRACES_SAMPLE_RATE = Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE) || 0;

// query string 中屬於敏感資訊的參數，送出前一律遮蔽
const SENSITIVE_QUERY_KEYS = ["token", "jwt", "auth", "authorization", "password", "pwd", "code"];

// 瀏覽器 / 套件噪音，不需要進 Sentry
const IGNORE_ERRORS = [
	"ResizeObserver loop limit exceeded",
	"ResizeObserver loop completed with undelivered notifications",
	"Non-Error promise rejection captured",
];

const DENY_URLS = [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i];

export const isSentryEnabled = () => Boolean(DSN);

/**
 * 把 url query 中的 token 之類參數換成 [Filtered]
 */
export const maskSensitiveUrl = (url) => {
	if (!url || typeof url !== "string") return url;
	const [path, queryString] = url.split("?");
	if (!queryString) return url;

	const maskedQueryString = queryString
		.split("&")
		.map((pair) => {
			const [key] = pair.split("=");
			return SENSITIVE_QUERY_KEYS.includes(decodeURIComponent(key || "").toLowerCase()) ? `${key}=[Filtered]` : pair;
		})
		.join("&");

	return `${path}?${maskedQueryString}`;
};

/**
 * 把路徑上的 uid / 流水號收斂成 {id}，讓同一支 API 的錯誤聚合成同一個 issue
 */
const generalizePath = (url) => {
	if (!url) return "unknown";
	const [path] = url.split("?");
	return path
		.replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "/{id}")
		.replace(/\/\d+(?=\/|$)/g, "/{id}");
};

const scrubEvent = (event) => {
	if (event.request) {
		// cookie 內有登入 token、data 是 request body，兩者都可能帶使用者資料
		delete event.request.cookies;
		delete event.request.data;

		if (event.request.url) event.request.url = maskSensitiveUrl(event.request.url);

		if (event.request.headers) {
			["Authorization", "authorization", "Cookie", "cookie"].forEach((key) => {
				delete event.request.headers[key];
			});
		}
	}

	if (Array.isArray(event.breadcrumbs)) {
		event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
			const url = breadcrumb?.data?.url;
			if (!url) return breadcrumb;
			return { ...breadcrumb, data: { ...breadcrumb.data, url: maskSensitiveUrl(url) } };
		});
	}

	return event;
};

const initSentry = () => {
	if (!isSentryEnabled()) return;

	Sentry.init({
		dsn: DSN,
		environment: ENVIRONMENT,
		release: RELEASE || undefined,
		// 不自動帶 IP / cookie 等個資
		sendDefaultPii: false,
		integrations: TRACES_SAMPLE_RATE > 0 ? [new Sentry.BrowserTracing()] : [],
		tracesSampleRate: TRACES_SAMPLE_RATE,
		ignoreErrors: IGNORE_ERRORS,
		denyUrls: DENY_URLS,
		beforeSend: scrubEvent,
		beforeBreadcrumb: (breadcrumb) => {
			// 專案內多處 console.log 會印出登入者資料與 API 回傳內容，不留在 Sentry
			if (breadcrumb.category === "console") return null;
			return breadcrumb;
		},
	});
};

/**
 * 同步登入者識別到 Sentry。只送平台帳號當 id，不送姓名。
 * 傳入空值（登出）時清除。
 */
export const setSentryUser = ({ account, identity } = {}) => {
	if (!isSentryEnabled()) return;

	if (!account) {
		Sentry.setUser(null);
		Sentry.setTag("identity", undefined);
		return;
	}

	Sentry.setUser({ id: account });
	Sentry.setTag("identity", identity || "unknown");
};

/**
 * 手動上報錯誤，可帶 tag / extras / fingerprint
 */
export const captureError = (error, { tag, extras, fingerprint } = {}) => {
	if (!isSentryEnabled()) return;

	Sentry.withScope((scope) => {
		if (tag) scope.setTag("type", tag);
		if (extras) scope.setExtras(extras);
		if (fingerprint) scope.setFingerprint(fingerprint);
		Sentry.captureException(error);
	});
};

const reportAxiosError = (error) => {
	const { method = "get", url = "" } = error?.config || {};
	const status = error?.response?.status;

	// 401 由登入流程處理，其餘 4xx 多為預期內的業務錯誤，都不上報
	if (status && status < 500) return;

	const upperCaseMethod = method.toUpperCase();
	const path = generalizePath(url);

	captureError(error, {
		tag: status ? "api_error" : "network_error",
		fingerprint: [status ? "api-error" : "network-error", upperCaseMethod, path, String(status || "network")],
		// 只留定位問題需要的資訊，不放 response body
		extras: {
			method: upperCaseMethod,
			url: maskSensitiveUrl(url),
			status: status || null,
		},
	});
};

const installAxiosErrorReporter = () => {
	if (!isSentryEnabled()) return;

	const handlerId = axios.interceptors.response.use(undefined, (error) => {
		reportAxiosError(error);
		return Promise.reject(error);
	});

	// common-components 的 interceptor 會把 error 改寫成字串，
	// 所以這支一定要排在最前面才拿得到原始的 axios error。
	const { handlers } = axios.interceptors.response;
	if (Array.isArray(handlers) && handlers.length > 1 && handlerId > 0) {
		handlers.unshift(handlers.splice(handlerId, 1)[0]);
	}
};

initSentry();
installAxiosErrorReporter();

export { Sentry };
